import React from 'react';
import ErrorBoundary from './ErrorBoundary';

/**
 * FretboardErrorBoundary - Specialized error boundary for Fretboard component
 * Provides specific fallback UI for fretboard rendering errors
 */
export function FretboardErrorBoundary({ children }: { children: React.ReactNode }) {
  const fretboardFallback = (
    <div className="fretboard-container">
      <div className="error-boundary">
        <div className="error-boundary-content">
          <div className="error-icon">🎸</div>
          <h3 className="error-title">Fretboard Rendering Error</h3>
          <p className="error-message">
            The fretboard couldn't be displayed. This might be due to an invalid arrangement or display issue.
          </p>
          <div className="error-actions">
            <button 
              className="retry-button primary"
              onClick={() => window.location.reload()}
              type="button"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary fallback={fretboardFallback}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * PaymentErrorBoundary - Specialized error boundary for payment/auth components
 * Provides specific fallback UI for payment processing errors
 */
export function PaymentErrorBoundary({ children }: { children: React.ReactNode }) {
  const paymentFallback = (
    <div className="error-boundary">
      <div className="error-boundary-content">
        <div className="error-icon">💳</div>
        <h3 className="error-title">Payment System Error</h3>
        <p className="error-message">
          There was an issue with the payment system. Your account is safe and no charges were made.
        </p>
        <div className="error-actions">
          <button 
            className="retry-button primary"
            onClick={() => window.location.reload()}
            type="button"
          >
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary fallback={paymentFallback}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * ControlPanelErrorBoundary - Specialized error boundary for control panel
 * Provides specific fallback UI for control panel errors
 */
export function ControlPanelErrorBoundary({ children }: { children: React.ReactNode }) {
  const controlPanelFallback = (
    <div className="control-panel">
      <div className="error-boundary">
        <div className="error-boundary-content">
          <div className="error-icon">🎛️</div>
          <h3 className="error-title">Control Panel Error</h3>
          <p className="error-message">
            The control panel encountered an error. You can still use the fretboard.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary 
      fallback={controlPanelFallback}
      resetOnPropsChange={true}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * BottomBarErrorBoundary - Specialized error boundary for bottom bar
 * Provides specific fallback UI for bottom bar errors
 */
export function BottomBarErrorBoundary({ children }: { children: React.ReactNode }) {
  const bottomBarFallback = (
    <div className="bottom-bar">
      <div className="error-boundary">
        <div className="error-boundary-content">
          <div className="error-icon">🎵</div>
          <h3 className="error-title">Controls Error</h3>
          <p className="error-message">
            The control bar encountered an error. Try refreshing the page.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary 
      fallback={bottomBarFallback}
      resetOnPropsChange={true}
    >
      {children}
    </ErrorBoundary>
  );
}
